// ** react imports
import React from 'react';

const OverallProgressSection = ({ taxFile }) => {
  const getProgressColor = progress => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getProgressTextColor = progress => {
    const colorClass = getProgressColor(progress);
    return colorClass.includes('yellow-') ? 'text-black' : 'text-white';
  };

  const overallProgress = taxFile.overallProgress || 0;
  const completedSections = taxFile.completedSections || 0;
  const totalSections = taxFile.totalSections || 0;

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="h-full md:h-[95px] px-4 py-4 sm:px-6 sm:py-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-2.5">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 tracking-wide">
                PROGRESS OVERVIEW
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Track completion status and key milestones
              </p>
            </div>
          </div>

          {/* Progress Percentage - Stacked on mobile, inline on larger screens */}
          <div className="flex flex-col items-start sm:items-end">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400">
              {overallProgress}%
            </div>
            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Complete
            </div>
          </div>
        </div>
      </div>

      <div className="h-full md:h-[180px] p-4 sm:p-6 space-y-5 sm:space-y-6">
        <div className="grid grid-cols-1 gap-5 sm:gap-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex-1 flex justify-between items-center p-2.5 sm:p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Completed
              </span>
              <span className="text-base sm:text-lg font-bold text-green-600 dark:text-green-400">
                {completedSections}
              </span>
            </div>

            <div className="flex-1 flex justify-between items-center p-2.5 sm:p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Remaining
              </span>
              <span className="text-base sm:text-lg font-bold text-orange-600 dark:text-orange-400">
                {totalSections - completedSections}
              </span>
            </div>

            <div className="flex-1 flex justify-between items-center p-2.5 sm:p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Total
              </span>
              <span className="text-base sm:text-lg font-bold text-gray-600 dark:text-gray-400">
                {totalSections}
              </span>
            </div>
          </div>

          <div className="">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100">
                Completion Status
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {taxFile.progressDisplay ||
                  `${completedSections} out of ${totalSections} sections have been completed.`}
              </p>
            </div>

            <div className="relative">
              <div className="w-full bg-gray-200 rounded-full h-4 sm:h-4.5 dark:bg-gray-700 mt-2">
                <div
                  className={`h-4 sm:h-4.5 rounded-full transition-all duration-500 ${getProgressColor(
                    overallProgress
                  )}`}
                  style={{ width: `${overallProgress}%` }}
                ></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className={`text-xs font-medium drop-shadow-sm ${getProgressTextColor(
                    overallProgress
                  )}`}
                >
                  {overallProgress}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OverallProgressSection;
